import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles-route-portfolio.css';

export default function PortfolioRoute({
  entry,
  portfolioPageSize,
  parsePortfolioEntry,
  buildPortfolioSummary,
  formatSlug,
  getPageWindow,
  inferImageDimensionsFromUrl,
}) {
  const portfolio = useMemo(() => parsePortfolioEntry(entry), [entry, parsePortfolioEntry]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const galleryTopRef = useRef(null);

  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') {
      return portfolio.items;
    }

    return portfolio.items.filter((item) => item.categories.includes(activeCategory));
  }, [activeCategory, portfolio.items]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / portfolioPageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * portfolioPageSize;
  const pagedItems = filteredItems.slice(pageStart, pageStart + portfolioPageSize);
  const selectedIndex = filteredItems.findIndex((item) => item.id === selectedId);
  const selectedItem = selectedIndex >= 0 ? filteredItems[selectedIndex] : null;
  const selectedImageDimensions = inferImageDimensionsFromUrl(selectedItem?.fullImage || '');

  const handlePageChange = (nextPage) => {
    const clampedPage = Math.max(1, Math.min(totalPages, nextPage));
    setCurrentPage(clampedPage);

    if (galleryTopRef.current) {
      galleryTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedId(null);
  }, [activeCategory]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (!selectedItem) {
      document.body.style.overflow = '';
      return undefined;
    }

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setSelectedId(null);
      }

      if (event.key === 'ArrowRight' && filteredItems.length > 1) {
        setSelectedId(filteredItems[(selectedIndex + 1) % filteredItems.length].id);
      }

      if (event.key === 'ArrowLeft' && filteredItems.length > 1) {
        setSelectedId(filteredItems[(selectedIndex - 1 + filteredItems.length) % filteredItems.length].id);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeydown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [filteredItems, selectedIndex, selectedItem]);

  return (
    <>
      <section className="portfolio-page">
        <section className="portfolio-hero">
          <div className="portfolio-hero-copy">
            <p className="eyebrow">Portfolio</p>
            <h1>{portfolio.title}</h1>
            <p>{portfolio.description}</p>
          </div>

          <div className="portfolio-hero-stats">
            <div>
              <span>Examples</span>
              <strong>{portfolio.items.length}</strong>
            </div>
            <div>
              <span>Categories</span>
              <strong>{portfolio.categories.length - 1}</strong>
            </div>
            <div>
              <span>Page</span>
              <strong>
                {safePage} / {totalPages}
              </strong>
            </div>
          </div>
        </section>

        <section className="portfolio-browser" ref={galleryTopRef}>
          <div className="portfolio-browser-header">
            <div>
              <p className="eyebrow">Browse Examples</p>
              <h2>Filter by application and page through the work.</h2>
            </div>
            <p className="portfolio-results">
              Showing {filteredItems.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pagedItems.length, filteredItems.length)} of {filteredItems.length}
            </p>
          </div>

          <div className="portfolio-filters" aria-label="Portfolio categories">
            {portfolio.categories.map((category) => (
              <button
                key={category.slug}
                type="button"
                className={category.slug === activeCategory ? 'is-active' : ''}
                onClick={() => setActiveCategory(category.slug)}
              >
                <span>{category.label}</span>
                <small>{category.count}</small>
              </button>
            ))}
          </div>

          {pagedItems.length > 0 ? (
            <div className="portfolio-grid">
              {pagedItems.map((item) => {
                const imageDimensions = inferImageDimensionsFromUrl(item.image);

                return (
                  <button
                    key={item.id}
                    type="button"
                    className="portfolio-card"
                    onClick={() => setSelectedId(item.id)}
                  >
                    <div className="portfolio-card-image-wrap">
                      <img
                        src={item.image}
                        alt={item.alt}
                        loading="lazy"
                        width={imageDimensions?.width}
                        height={imageDimensions?.height}
                      />
                    </div>
                    <div className="portfolio-card-body">
                      <div className="portfolio-card-tags">
                        {item.categories.slice(0, 2).map((category) => (
                          <span key={category}>{portfolio.categoryLabels.get(category) || formatSlug(category)}</span>
                        ))}
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.description || buildPortfolioSummary(item, portfolio.categoryLabels)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="portfolio-empty">
              <h3>No examples match that category.</h3>
              <p>Choose another filter to view more Creating Margin portfolio examples.</p>
            </div>
          )}

          {totalPages > 1 ? (
            <div className="portfolio-pagination" aria-label="Portfolio pagination">
              <button type="button" onClick={() => handlePageChange(safePage - 1)} disabled={safePage === 1}>
                Previous
              </button>
              {getPageWindow(totalPages, safePage).map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={pageNumber === safePage ? 'is-active' : ''}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handlePageChange(safePage + 1)}
                disabled={safePage === totalPages}
              >
                Next
              </button>
            </div>
          ) : null}
        </section>

        <section className="portfolio-cta">
          <div>
            <p className="eyebrow">Need Something Similar?</p>
            <h2>Have additional questions that need answering?</h2>
            <p>We'll get back to you as soon as we can.</p>
          </div>
          <Link className="button-link" to={portfolio.ctaHref}>
            Contact Us
          </Link>
        </section>
      </section>

      {selectedItem ? (
        <div className="portfolio-modal" role="dialog" aria-modal="true" aria-label={selectedItem.title}>
          <button type="button" className="portfolio-modal-backdrop" onClick={() => setSelectedId(null)} aria-label="Close portfolio item" />
          <div className="portfolio-modal-panel">
            <button type="button" className="portfolio-modal-close" onClick={() => setSelectedId(null)} aria-label="Close portfolio item">
              Close
            </button>

            <div className="portfolio-modal-media">
              <img
                src={selectedItem.fullImage}
                alt={selectedItem.alt}
                width={selectedImageDimensions?.width}
                height={selectedImageDimensions?.height}
              />
            </div>

            <div className="portfolio-modal-copy">
              <div className="portfolio-card-tags">
                {selectedItem.categories.map((category) => (
                  <span key={category}>{portfolio.categoryLabels.get(category) || formatSlug(category)}</span>
                ))}
              </div>
              <h2>{selectedItem.title}</h2>
              <p>{selectedItem.description || buildPortfolioSummary(selectedItem, portfolio.categoryLabels)}</p>
              <div className="portfolio-modal-actions">
                <a href={selectedItem.fullImage} target="_blank" rel="noopener noreferrer">
                  Open Full Image
                </a>
                {filteredItems.length > 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setSelectedId(filteredItems[(selectedIndex - 1 + filteredItems.length) % filteredItems.length].id)}
                    >
                      Previous Example
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedId(filteredItems[(selectedIndex + 1) % filteredItems.length].id)}
                    >
                      Next Example
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
